import "./Reports.css";
import "../theme/GlobalStyles.css";
import { IonContent, IonHeader, IonPage, IonButton, IonCheckbox, IonLabel } from "@ionic/react";
import { useContext, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import { Report } from "../utils/Interfaces";
import { UtilFunctions } from "../utils/UtilFunctions";
import { APIService } from "../network/APIService";
import { SingleValue } from "react-select";
import { TABLE_COLUMNS_REPORTS } from "../utils/Columns";
import ReportModal from "../components/ReportModal";
import Footer from "../components/Footer";
import Toolbar from "../components/Toolbar";
import RouteText from "../components/RouteText";
import CustomTable from "../components/CustomTable";
import AppContext from "../providers/AppContext";
import TableOptions, { SelectOption } from "../components/TableOptions";
import TableInstructionText from "../components/TableInstructionText";
import ReloadDataBtn from "../components/ReloadDataBtn";
import JSZip from "jszip";
import * as Papa from "papaparse";

const Reports: React.FC<RouteComponentProps> = props => {
  const appContext = useContext(AppContext);

  const initialDateFrom = new Date(appContext.reports.find(rep => parseInt(rep.id) === 7750)?.dateTimeCreated!);

  const [filteredReports, setFilteredReports] = useState(appContext.reports);

  // filters
  const [searchText, setSearchText] = useState("");
  const [dateFrom, setDateFrom] = useState(initialDateFrom);
  const [dateTo, setDateTo] = useState(new Date()); // initial state = current date
  const [countyFilter, setCountyFilter] = useState<SingleValue<SelectOption>>();
  const [policeAreaFilter, setPoliceAreaFilter] = useState<SingleValue<SelectOption>>();
  const [localAuthFilter, setLocalAuthFilter] = useState<SingleValue<SelectOption>>();
  const [mshFilter, setMshFilter] = useState<SingleValue<SelectOption>>();

  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report>();
  const [showHistoricReports, setShowHistoricReports] = useState(false);

  // kicks user back to login page if uid is not found in storage or it's not valid.
  useEffect(() => {
    if (appContext.jwt === "") {
      // browser refreshed so move back to landing
      props.history.replace("/");
    }
  }, []);

  useEffect(() => {
    let newFilteredReports: Report[] = [];

    // filter with search text - this needs to be filtered first because it searches the original list
    const text = searchText.toLowerCase().trim();
    if (!text || text === "") {
      newFilteredReports = appContext.reports;
    } else {
      newFilteredReports = appContext.reports.filter((value, index, array) => {
        // properties to search through
        const filterArray = [
          value.id.toString(),
          value.carWashName.toLowerCase(),
          value.county.toLowerCase(),
          value.policeArea.toLowerCase(),
          value.localAuth.toLowerCase(),
        ];
        return filterArray.some(value => value.includes(text));
      });
    }

    // filter with selected dates
    newFilteredReports = getReportsBetweenDateRange(newFilteredReports, dateFrom, dateTo);

    // filter with dropdown selectors
    if (countyFilter) {
      newFilteredReports = newFilteredReports.filter(rep => rep.county === countyFilter?.value);
    }
    if (policeAreaFilter) {
      newFilteredReports = newFilteredReports.filter(rep => rep.policeArea === policeAreaFilter?.value);
    }
    if (localAuthFilter) {
      newFilteredReports = newFilteredReports.filter(rep => rep.localAuth === localAuthFilter?.value);
    }
    if (mshFilter) {
      newFilteredReports = newFilteredReports.filter(rep => rep.sentMSH === mshFilter?.value);
    }

    // hide historic reports
    if (!showHistoricReports) newFilteredReports = newFilteredReports.filter(it => parseInt(it.id) > 7732);

    // finally, update displayed reports
    setFilteredReports(newFilteredReports);
  }, [searchText, dateFrom, dateTo, countyFilter, policeAreaFilter, localAuthFilter, mshFilter]);

  const formatAddressForExport = (obj: any) => {
    let resultString = "";
    const entries = Object.entries(obj);
    entries.forEach(entry => {
      resultString += entry[0] + ": " + entry[1] + "\n";
    });

    return resultString;
  };

  const getPreDevelopmentReportsCSV = () => {
    return Papa.unparse(
      filteredReports
        .filter(rep => parseInt(rep.id) <= 7732)
        .map(rep => {
          return {
            Id: rep.id,
            "Car Wash Name": rep.carWashName,
            County: rep.county,
            Latitude: rep.latitude,
            Longitude: rep.longitude,
            "Local Auth": rep.localAuth,
            "Police Area": rep.policeArea,
            Score: rep.score,
            "Sent MSH": rep.sentMSH,
            "Show MSH": rep.showMSH,
            "Date Time Created": UtilFunctions.formatDate(rep.dateTimeCreated),
            "Q: Cash Only": rep.questionAndAnswers.find(qa => qa.question === "Cash Only")?.answer ?? "",
            "Q: Appeared Recently":
              rep.questionAndAnswers.find(qa => qa.question === "Appeared Recently")?.answer ?? "",
            "Q: Child Wokers": rep.questionAndAnswers.find(qa => qa.question === "Child Workers")?.answer ?? "",
            "Q: Controlling Manager":
              rep.questionAndAnswers.find(qa => qa.question === "Controlling Manager")?.answer ?? "",
            "Q: Duration": rep.questionAndAnswers.find(qa => qa.question === "Duration")?.answer ?? "",
            "Q: Fearful Workers": rep.questionAndAnswers.find(qa => qa.question === "Fearful Workers")?.answer ?? "",
            "Q: How Much": rep.questionAndAnswers.find(qa => qa.question === "HowMuch")?.answer ?? "",
            "Q: Living On Site": rep.questionAndAnswers.find(qa => qa.question === "LivingOnSite")?.answer ?? "",
            "Q: Name": rep.questionAndAnswers.find(qa => qa.question === "Name")?.answer ?? "",
            "Q: Num Workers": rep.questionAndAnswers.find(qa => qa.question === "NumWorkers")?.answer ?? "",
            "Q: Pay Manager": rep.questionAndAnswers.find(qa => qa.question === "PayManager")?.answer ?? "",
            "Q: Receipt": rep.questionAndAnswers.find(qa => qa.question === "Receipt")?.answer ?? "",
            "Q: Report User": rep.questionAndAnswers.find(qa => qa.question === "ReportUser")?.answer ?? "",
            "Q: Suitable Clothing": rep.questionAndAnswers.find(qa => qa.question === "SuitableClothing")?.answer ?? "",
          };
        })
    );
  };

  const getPostDevelopmentReportsCSV = () => {
    return Papa.unparse(
      filteredReports
        .filter(rep => parseInt(rep.id) > 7732)
        .map(rep => {
          const res: any = {
            Id: rep.id,
            "Car Wash Name": rep.carWashName,
            County: rep.county,
            Latitude: rep.latitude,
            Longitude: rep.longitude,
            "Local Auth": rep.localAuth,
            "Police Area": rep.policeArea,
            Score: rep.score,
            "Sent MSH": rep.sentMSH,
            "Show MSH": rep.showMSH,
            "Date Time Created": UtilFunctions.formatDate(rep.dateTimeCreated),
            "Detected Address":
              rep.manualAddress?.streetName === "" ? formatAddressForExport(rep.detectedAddress) : "n/a",
            "Manual Address": rep.manualAddress?.streetName !== "" ? formatAddressForExport(rep.manualAddress) : "n/a",
          };

          // add questions and answers for post development
          rep.questionAndAnswers.forEach(item => {
            res[`Q: ${item.question}`] = item.answer;
          });

          return res;
        })
    );
  };

  const getCombinedReportsCSV = () => {
    return Papa.unparse(
      filteredReports.map(rep => {
        return {
          Id: rep.id,
          "Car Wash Name": rep.carWashName,
          County: rep.county,
          Latitude: rep.latitude,
          Longitude: rep.longitude,
          "Local Auth": rep.localAuth,
          "Police Area": rep.policeArea,
          Score: rep.score,
          "Sent MSH": rep.sentMSH,
          "Show MSH": rep.showMSH,
          "Date Time Created": UtilFunctions.formatDate(rep.dateTimeCreated),
          "Detected Address":
            rep.manualAddress?.streetName === "" && parseInt(rep.id) > 7732
              ? formatAddressForExport(rep.detectedAddress)
              : "n/a",
          "Manual Address": rep.manualAddress?.streetName !== "" ? formatAddressForExport(rep.manualAddress) : "n/a",
          // pre-dev report questions
          "Q: Cash Only": rep.questionAndAnswers.find(qa => qa.question === "Cash Only")?.answer ?? "",
          "Q: Appeared Recently": rep.questionAndAnswers.find(qa => qa.question === "Appeared Recently")?.answer ?? "",
          "Q: Child Wokers": rep.questionAndAnswers.find(qa => qa.question === "Child Workers")?.answer ?? "",
          "Q: Controlling Manager":
            rep.questionAndAnswers.find(qa => qa.question === "Controlling Manager")?.answer ?? "",
          "Q: Duration": rep.questionAndAnswers.find(qa => qa.question === "Duration")?.answer ?? "",
          "Q: Fearful Workers": rep.questionAndAnswers.find(qa => qa.question === "Fearful Workers")?.answer ?? "",
          "Q: How Much": rep.questionAndAnswers.find(qa => qa.question === "HowMuch")?.answer ?? "",
          "Q: Living On Site": rep.questionAndAnswers.find(qa => qa.question === "LivingOnSite")?.answer ?? "",
          "Q: Name": rep.questionAndAnswers.find(qa => qa.question === "Name")?.answer ?? "",
          "Q: Num Workers": rep.questionAndAnswers.find(qa => qa.question === "NumWorkers")?.answer ?? "",
          "Q: Pay Manager": rep.questionAndAnswers.find(qa => qa.question === "PayManager")?.answer ?? "",
          "Q: Receipt": rep.questionAndAnswers.find(qa => qa.question === "Receipt")?.answer ?? "",
          "Q: Report User": rep.questionAndAnswers.find(qa => qa.question === "ReportUser")?.answer ?? "",
          "Q: Suitable Clothing": rep.questionAndAnswers.find(qa => qa.question === "SuitableClothing")?.answer ?? "",
          // post-dev report questions
          "Q: What is the name of the car wash?":
            rep.questionAndAnswers.find(qa => qa.question === "What is the name of the car wash?")?.answer ?? "",
          "Q: Is the car wash new or has it recently changed its name or ownership?":
            rep.questionAndAnswers.find(
              qa =>
                qa.question ===
                "Is the car wash new or has it recently changed its name or ownership (in the last month)?"
            )?.answer ?? "",
          "Q: What type of location is the car wash in?":
            rep.questionAndAnswers.find(
              qa =>
                qa.question ===
                "What type of location is the car wash in? (e.g. Petrol station, supermarket, disused site)"
            )?.answer ?? "",
          "Q: What Type of Car Wash is it?":
            rep.questionAndAnswers.find(qa => qa.question === "What Type of Car Wash is it?")?.answer ?? "",
          "Q: Please tell us about any protective clothing the workers are wearing":
            rep.questionAndAnswers.find(
              qa => qa.question === "Please tell us about any protective clothing the workers are wearing"
            )?.answer ?? "",
          "Q: Did you see anything that makes you think workers may be living at the car wash?":
            rep.questionAndAnswers.find(
              qa => qa.question === "Did you see anything that makes you think workers may be living at the car wash?"
            )?.answer ?? "",
          "Q: Does anyone appear over-controlling or intimidating?":
            rep.questionAndAnswers.find(qa => qa.question === "Does anyone appear over-controlling or intimidating?")
              ?.answer ?? "",
          "Q: Does the body language of workers appear fearful or withdrawn?":
            rep.questionAndAnswers.find(
              qa => qa.question === "Does the body language of workers appear fearful or withdrawn?"
            )?.answer ?? "",
          "Q: Are the workers able to speak English?":
            rep.questionAndAnswers.find(qa => qa.question === "Are the workers able to speak English?")?.answer ?? "",
          "Q: Do there appear to be under 18s working at the site?":
            rep.questionAndAnswers.find(qa => qa.question === "Do there appear to be under 18s working at the site?")
              ?.answer ?? "",
          "Q: Do there appear to be under 16s working at the site?":
            rep.questionAndAnswers.find(qa => qa.question === "Do there appear to be under 16s working at the site?")
              ?.answer ?? "",
          "Q: What size is your car?":
            rep.questionAndAnswers.find(qa => qa.question === "What size is your car?")?.answer ?? "",
          "Q: What type of wash did you get?":
            rep.questionAndAnswers.find(qa => qa.question === "What type of wash did you get?")?.answer ?? "",
          "Q: How much did you pay?":
            rep.questionAndAnswers.find(qa => qa.question === "How much did you pay?")?.answer ?? "",
          "Q: How many workers cleaned your car?":
            rep.questionAndAnswers.find(qa => qa.question === "How many workers cleaned your car?")?.answer ?? "",
          "Q: Were you able to pay by card?":
            rep.questionAndAnswers.find(qa => qa.question === "Were you able to pay by card?")?.answer ?? "",
          "Q: Did they offer a receipt?":
            rep.questionAndAnswers.find(qa => qa.question === "Did they offer a receipt?")?.answer ?? "",
          "Q: Did you have to pay a manager?":
            rep.questionAndAnswers.find(qa => qa.question === "Did you have to pay a manager?")?.answer ?? "",
          "Q: Please tell us about anything you have seen that you are concerned about. For example, the behaviour of the boss and/or workers, unusual activity around the site, workers being delivered to and from the site, frequent rapid turnover of staff, wellbeing concerns about minors.":
            rep.questionAndAnswers.find(
              qa =>
                qa.question ===
                "Please tell us about anything you have seen that you are concerned about. For example, the behaviour of the boss and/or workers, unusual activity around the site, workers being delivered to and from the site, frequent rapid turnover of staff, wellbeing concerns about minors."
            )?.answer ?? "",
        };
      })
    );
  };

  const handleExportCSV = async () => {
    const zip = new JSZip();

    const preDevelopmentReportFound = filteredReports.find(rep => parseInt(rep.id) <= 7732);
    const postDevelopmentReportFound = filteredReports.find(rep => parseInt(rep.id) > 7732);
    const bothReportTypesFound =
      filteredReports.find(rep => parseInt(rep.id) <= 7732) && filteredReports.find(rep => parseInt(rep.id) > 7732);

    // Add necessary files to zip
    if (preDevelopmentReportFound) {
      const csv = getPreDevelopmentReportsCSV();
      const file = new Blob([csv]);
      zip.file("Pre-Development-Reports.csv", file);
    }
    if (postDevelopmentReportFound) {
      const csv = getPostDevelopmentReportsCSV();
      const file = new Blob([csv]);
      zip.file("Post-Development-Reports.csv", file);
    }
    if (bothReportTypesFound) {
      const csv = getCombinedReportsCSV();
      const file = new Blob([csv]);
      zip.file("Pre-And-Post-Development-Reports.csv", file);
    }

    // Finalize zip file and activate download event
    const content = await zip.generateAsync({ type: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(content);
    link.download =
      "Reports-" +
      UtilFunctions.formatDateForFile(dateFrom) +
      "-to-" +
      UtilFunctions.formatDateForFile(dateTo) +
      ".zip";
    link.click();
  };

  const reloadReports = async () => {
    const reports = await APIService.fetchReports(appContext.currentUser?.id!, appContext.jwt);
    if (reports) {
      // reset filters
      setCountyFilter(null);
      setPoliceAreaFilter(null);
      setLocalAuthFilter(null);
      setMshFilter(null);
      setSearchText("");
      setDateFrom(getEarliestDateFromReports(reports));
      setDateTo(new Date());

      setFilteredReports(reports);
    }
  };

  function getReportsBetweenDateRange(reps: Report[], dateFrom: Date, dateTo: Date) {
    return reps.filter(
      (report, index, array) => report.dateTimeCreated >= dateFrom && report.dateTimeCreated <= dateTo
    );
  }

  function getEarliestDateFromReports(reps: Report[]) {
    let earliestDate: Date = new Date(); // init to today
    reps.forEach((value, index, array) => {
      if (value.dateTimeCreated < earliestDate) {
        earliestDate = value.dateTimeCreated;
      }
    });
    return earliestDate;
  }

  return (
    <IonPage>
      <IonHeader>
        <Toolbar props={props} />
      </IonHeader>
      <IonContent>
        <div className="pageContainer">
          <div className="pageContentContainer">
            <RouteText
              routes={[{ name: "Dashboard", route: "/" }]}
              currentLocation="Car Wash Reports"
              routeProps={props}
            />
            <h1
              id="reportsHeader"
              className="pageHeader"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}
            >
              Car Wash Reports <span style={{ fontSize: 16 }}>{filteredReports.length} results</span>
            </h1>
            <TableOptions
              countyFilter={countyFilter!}
              policeAreaFilter={policeAreaFilter!}
              localAuthFilter={localAuthFilter!}
              mshFilter={mshFilter!}
              reportsDisplayed={filteredReports}
              dateFrom={dateFrom}
              dateTo={dateTo}
              setDateFrom={setDateFrom}
              setDateTo={setDateTo}
              searchText={searchText}
              handleSearch={(text: string) => setSearchText(text)}
              handleCountyChange={newValue => setCountyFilter(newValue)}
              handlePoliceAreaChange={newValue => setPoliceAreaFilter(newValue)}
              handleLocalAuthChange={newValue => setLocalAuthFilter(newValue)}
              handleMshChange={newValue => setMshFilter(newValue)}
            />
            <div className="numberOfResultsContainer">
              <div style={{}}>
                <div
                  style={{
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <IonCheckbox
                    checked={showHistoricReports}
                    onIonChange={e => {
                      // restict date range when this checkbox is used
                      let dateFrom: Date;
                      if (e.detail.checked) {
                        dateFrom = appContext.reports.find(rep => parseInt(rep.id) === 1)?.dateTimeCreated!;
                      } else {
                        dateFrom = appContext.reports.find(rep => parseInt(rep.id) === 7750)?.dateTimeCreated!;
                      }
                      const dateTo = appContext.reports[appContext.reports.length - 1].dateTimeCreated;

                      setDateFrom(dateFrom);
                      setDateTo(dateTo);
                      setShowHistoricReports(e.detail.checked);
                    }}
                  />
                  <IonLabel className="checkboxLabel">Show historic reports</IonLabel>
                </div>
              </div>
              <div>
                <ReloadDataBtn handleClick={reloadReports} />
                <IonButton disabled={filteredReports.length === 0} onClick={handleExportCSV} className="yellowBtn">
                  Export CSV
                </IonButton>
              </div>
            </div>
            <CustomTable
              cols={TABLE_COLUMNS_REPORTS}
              rowData={filteredReports}
              handleSelectItem={report => {
                setSelectedReport(report as Report);
                setShowModal(true);
              }}
              defaultSort={[{ id: "dateTimeCreated", desc: true }]}
            />
            <ReportModal
              selectedReport={selectedReport}
              showModal={showModal}
              setShowModal={show => setShowModal(show)}
            />
            <TableInstructionText
              text={`Click a header to sort (shift+click to use more than one).${"\n"}Search input looks for matches to id, name, county, police area, or local authority.`}
            />
          </div>
          <Footer />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Reports;
